// components/applicationProfile/common/flow.ts

export type ApplicationType = 'personal' | 'guarantor';

export type FlowSubstep = {
  id: string;
  label: string;
};

export type FlowStep = {
  tab: string;
  label: string;
  substeps: FlowSubstep[];
};

export type NonEmptyFlow = readonly [FlowStep, ...FlowStep[]];

export type FlowOptions = {
  /**
   * If `unit` is present in the URL, Confirm & apply has a shortened substep list:
   * only Application fee + Policy & compliance (CA4/CA5) are shown (CA1 remains the root screen).
   */
  unit?: string | null;
  /**
   * Partner-specific flow tweaks (from URL param `partner`).
   * Example: partner=revona swaps CA3 to "Customer survey".
   */
  partner?: string | null;
  /**
   * Set when reopening an existing application from the applications list.
   * Only used to tell an application apart from the applicant profile.
   */
  editId?: string | null;
  /**
   * Set when the current seat is an invited co-applicant (not the primary
   * leaseholder). Co-applicants skip Move-in (PHI5) and Resident info (PHI6) —
   * those belong to the primary leaseholder.
   *
   * Sourced from the backend `is_co_applicant` flag at boot and threaded via the
   * `isCoApplicant` URL param, mirroring `applicationType`.
   */
  isCoApplicant?: boolean;
};

function hasValue(v: string | null | undefined) {
  return Boolean(v && v.trim().length > 0);
}

/**
 * The applicant profile is the bare `/profile/application-profile` entry from the
 * profile page: a reusable profile the renter fills in ahead of time, not an
 * application. See `applyApplicantProfileRules` for what that removes.
 *
 * Anything carrying `unit`, `editId` or `partner` is a real application and keeps
 * the full flow, PHI0 and Confirm & apply included.
 */
export function isApplicantProfileFlow(opts?: FlowOptions): boolean {
  return !hasValue(opts?.unit) && !hasValue(opts?.editId) && !hasValue(opts?.partner);
}

/**
 * Single place that turns the URL into `FlowOptions`.
 *
 * Every flow lookup must go through this: the applicant-profile/application split
 * is derived from several params at once, so a call site that forgets one would
 * silently resolve the wrong flow.
 */
export function flowOptionsFromSearchParams(sp: { get(key: string): string | null }): FlowOptions {
  return {
    unit: sp.get('unit'),
    partner: sp.get('partner'),
    editId: sp.get('editId'),
    isCoApplicant: sp.get('isCoApplicant') === 'true',
  };
}

/**
 * Single source of truth for flow structure + display labels.
 */
/**
 * Personal application flow
 */
export const PERSONAL_FLOW: NonEmptyFlow = [
  {
    tab: 'basic-info',
    label: 'Personal and household info',
    substeps: [
      { id: 'PHI0', label: 'Who are you applying as?' },
      // PHI1 is the "overview/root" for the main step (we hide it in the left substep list)
      { id: 'PHI1', label: 'Personal and household info' },
      { id: 'PHI2', label: 'Introduce yourself' },
      { id: 'PHI3', label: 'Other personal details' },
      { id: 'PHI4', label: 'Emergency contact' },
      { id: 'PHI5', label: 'Move-in' },
      { id: 'PHI6', label: 'Resident info' },
      { id: 'PHI7', label: 'Pets' },
    ],
  },
  {
    tab: 'rental-history',
    label: 'Rental history',
    substeps: [
      { id: 'RH1', label: 'Rental history' },
      { id: 'RH2', label: 'Rental history' },
      { id: 'RH3', label: 'Additional info' },
    ],
  },
  {
    tab: 'finance',
    label: 'Financial info',
    substeps: [
      { id: 'F1', label: 'Financial info' },
      { id: 'F2', label: 'Credit score' },
      { id: 'F3', label: 'Employment verification' },
      { id: 'F4', label: 'Proof of income' },
      { id: 'F5', label: 'Guarantor info' },
    ],
  },
  {
    tab: 'documents',
    label: 'Supporting documents',
    substeps: [
      { id: 'D1', label: 'Supporting documents' },
      { id: 'D2', label: 'Required documents' },
      { id: 'D3', label: 'Additional documents' },
    ],
  },
  {
    tab: 'confirmApply',
    label: 'Confirm and apply',
    substeps: [
      { id: 'CA1', label: 'Confirm and apply' },
      { id: 'CA2', label: 'Apartment and lease info' },
      { id: 'CA3', label: 'Recipients' },
      { id: 'CA4', label: 'Application fee' },
      { id: 'CA5', label: 'Policy and compliance' },
    ],
  },
];

/**
 * Guarantor application flow
 */
export const GUARANTOR_FLOW: NonEmptyFlow = [
  {
    tab: 'basic-info',
    label: 'Personal and household info',
    substeps: [
      { id: 'PHI0', label: 'Who are you applying as?' },
      { id: 'PHI1', label: 'Personal and household info' },
      { id: 'PHI2', label: 'Introduce yourself' },
      { id: 'PHI3', label: 'Other personal details' },
    ],
  },
  {
    tab: 'finance',
    label: 'Financial info',
    substeps: [
      { id: 'F1', label: 'Financial info' },
      { id: 'F2', label: 'Credit score' },
      { id: 'F3', label: 'Employment verification' },
      { id: 'F4', label: 'Proof of income' },
    ],
  },
  {
    tab: 'documents',
    label: 'Supporting documents',
    substeps: [
      { id: 'D1', label: 'Supporting documents' },
      { id: 'D2', label: 'Required documents' },
      { id: 'D3', label: 'Additional documents' },
    ],
  },
  {
    tab: 'confirmApply',
    label: 'Confirm and apply',
    substeps: [
      { id: 'CA1', label: 'Confirm and apply' },
      { id: 'CA2', label: 'Apartment and lease info' },
      { id: 'CA3', label: 'Recipients' },
      { id: 'CA4', label: 'Application fee' },
      { id: 'CA5', label: 'Policy and compliance' },
    ],
  },
];

function applyConfirmApplyUnitRules(flow: NonEmptyFlow, opts?: FlowOptions): NonEmptyFlow {
  const hasUnit = Boolean(opts?.unit);
  if (!hasUnit) return flow;

  const keep = new Set(['CA1', 'CA4', 'CA5']);
  const next = flow.map(step => {
    if (step.tab !== 'confirmApply') return step;
    return {
      ...step,
      substeps: step.substeps.filter(s => keep.has(s.id)),
    };
  });
  // `flow` is a non-empty tuple and `.map()` preserves length, but TS loses that fact.
  const [first, ...rest] = next;
  return [first!, ...rest] as NonEmptyFlow;
}

function applyPartnerRules(flow: NonEmptyFlow, opts?: FlowOptions): NonEmptyFlow {
  const partner = (opts?.partner ?? '').toString().toLowerCase();
  if (partner !== 'revona') return flow;

  const next = flow.map(step => {
    if (step.tab !== 'confirmApply') return step;
    return {
      ...step,
      substeps: step.substeps.map(s => (s.id === 'CA3' ? { ...s, label: 'Customer survey' } : s)),
    };
  });
  const [first, ...rest] = next;
  return [first!, ...rest] as NonEmptyFlow;
}

/** Substeps that only make sense once a unit is attached. */
const APPLICATION_ONLY_SUBSTEPS = new Set([
  // "Who are you applying as?" — whether you need a guarantor depends on the unit.
  'PHI0',
  // "When will you move in?" — the renter adjusts this to the unit's availability.
  'PHI5',
  // "Guarantor info" — whether a guarantor is needed depends on the unit's price.
  'F5',
]);

/**
 * Applicant profile is a reusable profile, not an application, so it drops:
 * - the application-only substeps above, and
 * - Confirm and apply — nothing to confirm, no recipients, no fee. It ends at
 *   Supporting documents.
 */
function applyApplicantProfileRules(flow: NonEmptyFlow, opts?: FlowOptions): NonEmptyFlow {
  if (!isApplicantProfileFlow(opts)) return flow;

  const next = flow
    .filter(step => step.tab !== 'confirmApply')
    .map(step => ({
      ...step,
      substeps: step.substeps.filter(s => !APPLICATION_ONLY_SUBSTEPS.has(s.id)),
    }));
  const [first, ...rest] = next;
  // basic-info always survives the filter, so the tuple stays non-empty.
  return [first!, ...rest] as NonEmptyFlow;
}

/**
 * Substeps an invited co-applicant doesn't fill in — they belong to the primary
 * leaseholder, who sets the move-in date and lists the residents/co-applicants.
 */
const CO_APPLICANT_SKIPPED_SUBSTEPS = new Set([
  // "Move-in" — the primary leaseholder owns the household move-in date.
  'PHI5',
  // "Resident info" — the primary leaseholder lists everyone on the lease.
  'PHI6',
]);

/**
 * Co-applicants share the personal flow but skip Move-in (PHI5) and Resident
 * info (PHI6). Removing them here keeps Next/Back, the stepper, progress and
 * submit-validation consistent, since they all read this same filtered list.
 */
function applyCoApplicantRules(flow: NonEmptyFlow, opts?: FlowOptions): NonEmptyFlow {
  if (!opts?.isCoApplicant) return flow;

  const next = flow.map(step => {
    if (step.tab !== 'basic-info') return step;
    return {
      ...step,
      substeps: step.substeps.filter(s => !CO_APPLICANT_SKIPPED_SUBSTEPS.has(s.id)),
    };
  });
  const [first, ...rest] = next;
  // basic-info always survives the filter, so the tuple stays non-empty.
  return [first!, ...rest] as NonEmptyFlow;
}

export function getFlow(
  applicationType: ApplicationType = 'personal',
  opts?: FlowOptions,
): NonEmptyFlow {
  const base = applicationType === 'guarantor' ? GUARANTOR_FLOW : PERSONAL_FLOW;
  return applyCoApplicantRules(
    applyApplicantProfileRules(
      applyPartnerRules(applyConfirmApplyUnitRules(base, opts), opts),
      opts,
    ),
    opts,
  );
}

export function getFirstStep(applicationType: ApplicationType = 'personal', opts?: FlowOptions) {
  const flow = getFlow(applicationType, opts);

  // now fully type-safe
  return {
    tab: flow[0].tab,
    substep: flow[0].substeps[0]!.id,
  };
}

/**
 * Find index of tab in flow
 */
export function getTabIndex(
  tab: string,
  applicationType: ApplicationType,
  opts?: FlowOptions,
): number {
  return getFlow(applicationType, opts).findIndex(step => step.tab === tab);
}

/**
 * Find next tab + substep (used for Next button)
 */
export function getNextStep(
  tab: string,
  substep: string,
  applicationType: ApplicationType,
  opts?: FlowOptions,
) {
  const flow = getFlow(applicationType, opts);

  const tabIndex = flow.findIndex(step => step.tab === tab);
  if (tabIndex === -1) return null;

  const currentStep = flow[tabIndex];
  if (!currentStep) return null; // ✅ TS-safe guard

  const substeps = currentStep.substeps;
  const subIndex = substeps.findIndex(s => s.id === substep);

  // next substep in same tab
  if (subIndex !== -1 && subIndex < substeps.length - 1) {
    return { tab, substep: substeps[subIndex + 1]!.id };
  }

  // move to next tab
  const nextStep = flow[tabIndex + 1];
  if (!nextStep) return null;

  return { tab: nextStep.tab, substep: nextStep.substeps[0]!.id };
}

/**
 * Find previous tab + substep (used for Back button)
 */
export function getPreviousStep(
  tab: string,
  substep: string,
  applicationType: ApplicationType,
  opts?: FlowOptions,
) {
  const flow = getFlow(applicationType, opts);

  const tabIndex = flow.findIndex(step => step.tab === tab);
  if (tabIndex === -1) return null;

  const currentStep = flow[tabIndex];
  if (!currentStep) return null; // ✅ TS-safe guard

  const substeps = currentStep.substeps;
  const subIndex = substeps.findIndex(s => s.id === substep);

  // previous substep in same tab
  if (subIndex > 0) {
    return {
      tab,
      substep: substeps[subIndex - 1]!.id,
    };
  }

  // if substep not found OR we're at the first substep,
  // move to previous tab
  const prevStep = flow[tabIndex - 1];
  if (!prevStep) return null;

  return {
    tab: prevStep.tab,
    substep: prevStep.substeps[prevStep.substeps.length - 1]!.id,
  };
}
